import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Validation from '../../../shared/Validation';
import './PasswordReset.scss';
import Logout from '../../../shared/Logout/Logout';
import Header from '../../../shared/Header/Header';
import Button from '../../../shared/Button/Button';
import FloatingError from '../../../shared/FloatingError/FloatingError';
import classNames from 'classnames';
import imprivataIcon from '../Icons/imprivata.svg';

/**
 * PasswordReset prompt (not applicable in the instance of Microsoft AD FS)
 * @class PasswordReset
 * @extends {Component}
 */
class PasswordReset extends Component {
  static propTypes = {
    /** Header image */
    headerImage: PropTypes.string,
    /** Header title */
    headerTitle: PropTypes.string,
    /** Function executed when password field changes. Typically password in
    state is updated. */
    onInputChange: PropTypes.func,
    /** Function to execute when form is submitted. */
    onSubmit: PropTypes.func,
    /** Text displayed on the submit button. */
    submitText: PropTypes.string,
    /** Validation constraints object for validate.js
    (https://validatejs.org/#constraints) */
    validation: PropTypes.object,
    /** Whether the first field should automatically gain focus */
    autoFocus: PropTypes.bool,
    /** Which errors to show */
    displayErrors: PropTypes.array,
    /** The value of the password field */
    passwordValue: PropTypes.string,
    /** The value of the confirm password field */
    passwordConfirmValue: PropTypes.string,
    /** The component to be used for logout */
    logoutComponent: PropTypes.object,
    /** The DOM tree/component to be used for the footer */
    footer: PropTypes.object,
    /** Use Parkland custom UX */
    useCustomUX: PropTypes.bool,
  };
  static defaultProps = {
    headerTitle: 'Password expired',
    submitText: 'Reset password',
    validation: {
      password: {
        presence: true,
        length: { minimum: 1, },
      },
      passwordConfirm: {
        presence: true,
        length: { minimum: 1, },
        equality: {
          attribute: 'password',
          message: 'does not match',
        },
      },
    },
    autoFocus: false,
    displayErrors: [],
    passwordValue: '',
    passwordConfirmValue: '',
    onInputChange: () => void (0),
    logoutComponent: <Logout />,
    // Parkland custom UX
    useCustomUX: false,
  };
  constructor(props) {
    super(props);
    this.validation = new Validation(props.validation);
    this.state = {
      errors: '',
      showErrors: false,
      password: props.passwordValue,
      passwordConfirm: props.passwordConfirmValue,
      disableSubmit: true,
    };
  }
  componentDidUpdate(prevProps) {
    if (this.props.displayErrors !== prevProps.displayErrors) {
      this.focusFirstInput();
    }
    if (this.props.passwordValue !== prevProps.passwordValue ||
      this.props.passwordConfirmValue !== prevProps.passwordConfirmValue) {
      this.setState({
        password: this.props.passwordValue,
        passwordConfirm: this.props.passwordConfirmValue,
      });
    }
  };
  focusFirstInput = () => {
    this.passwordInput.focus();
  };
  errorHelp = (fieldName) => {
    const errors = this.validation.errorMessages();
    if (errors && this.state.showErrors) {
      const message = errors[fieldName];
      return (
        <span className='help-block'>{message}</span>
      );
    }
    return '';
  };
  changeInput = () => {
    const inputValues = {
      password: this.passwordInput.value,
      passwordConfirm: this.passwordConfirmInput.value,
    };
    this.validation.updateValues(inputValues);
    inputValues.disableSubmit = this.validation.hasErrors();
    inputValues.errors = this.validation.errorMessages();
    inputValues.showErrors = false;
    this.setState(inputValues);
    this.props.onInputChange(inputValues);
  };
  submitForm = (event) => {
    event.preventDefault();
    if (this.state.errors) {
      this.setState({ showErrors: true, });
    }
    else {
      this.setState({ showErrors: false, });
      this.props.onSubmit(this.state);
    }
  };
  render() {
    const formClasses = classNames({
      'form-control': true,
      'password': true,
      'form-error-outline': this.props.displayErrors.length > 0,
    });
    return (
      <div className='CIDRA'>
        {this.props.logoutComponent}
        {this.props.useCustomUX ?
          (<Header />)
          :
          (
            <div className='Header-Box'>
              <div className='Header-Box'>
                <img src={this.props.headerImage} className='Modality-Icon' alt='lock' />
                <div className='Header-Text'>
                  <span>{this.props.headerTitle}</span>
                </div>
                <div className='Header-Description'>
                  <span>{this.props.headerDescription}</span>
                </div>
              </div>
            </div>
          )
        }
        <div className='login-container'>
          <form id='PasswordReset_form'
            className='form'
            onSubmit={this.submitForm}
          >
            {this.props.displayErrors.length > 0 ?
              (
                <div>
                  <FloatingError messages={this.props.displayErrors} />
                </div>
              )
              : null
            }
            <div className='form-group CIDRA-Field'>
              <input
                autoFocus={this.props.autoFocus}
                type='password'
                id='PasswordReset_password_input'
                className={formClasses}
                placeholder='New password'
                name='password'
                ref={(input) => {
                  this.passwordInput = input;
                }}
                onChange={this.changeInput}
                value={this.state.password}
              />
              {this.errorHelp('password')}
            </div>
            <div className='form-group CIDRA-Field'>
              <input
                type='password'
                id='PasswordReset_password_confirm_input'
                className={formClasses}
                placeholder='Re-enter password'
                name='passwordConfirm'
                ref={(input) => {
                  this.passwordConfirmInput = input;
                }}
                onChange={this.changeInput}
                value={this.state.passwordConfirm}
              />
              {this.errorHelp('passwordConfirm')}
            </div>
            <div className='form-group CIDRA-Field'>
              {this.props.useCustomUX ?
                (
                  <Button
                    id='PasswordReset_submit_button'
                    disabled={this.state.disableSubmit}
                    onClick={this.submitForm}
                    submitText='Sign In'
                  />
                )
                :
                (
                  <input
                    className='btn btn-primary btn-block'
                    id='PasswordReset_submit_button'
                    type='submit'
                    value={this.props.submitText}
                    disabled={this.state.disableSubmit}
                    onClick={this.submitForm}
                  />
                )
              }
            </div>
          </form>
        </div>
        <hr className='Footer-hr' />
        <img className='Footer' src={imprivataIcon} alt='logo' />
      </div>
    );
  }
}

export default PasswordReset;
